/**
 * Display helpers. Pure functions, no imports, no side effects — safe to call
 * during render.
 */

type DateInput = string | number | Date | null | undefined;

/** Shown wherever a value is missing rather than zero. */
const EMPTY = "—"; // em dash

const MONTHS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/**
 * Integer cents to a price the store would print.
 *
 *   money(33300) -> "$333.00"
 *   money(125000) -> "$1,250.00"
 *
 * A missing price is an em dash, never "$0.00" — the two mean different things
 * on a product that has no listed price.
 */
export function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return EMPTY;

  const negative = cents < 0;
  const total = Math.round(Math.abs(cents));
  const whole = Math.floor(total / 100);
  const fraction = total % 100;

  return `${negative ? "-" : ""}$${group(whole)}.${fraction < 10 ? "0" : ""}${fraction}`;
}

/** Thousands separators, without leaning on Intl. */
function group(value: number): string {
  const digits = String(value);
  let out = "";
  for (let i = 0; i < digits.length; i += 1) {
    // A separator goes before every digit whose distance from the end is a
    // positive multiple of three.
    const fromEnd = digits.length - i;
    if (i > 0 && fromEnd % 3 === 0) out += ",";
    out += digits[i];
  }
  return out;
}

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Compact age of a timestamp.
 *
 *   relativeTime(twoHoursAgo) -> "2h ago"
 *   relativeTime(inTenMinutes) -> "in 10m"
 *
 * Unparseable or missing input gives an em dash, so it can be dropped straight
 * into a sentence without a guard.
 */
export function relativeTime(value: DateInput, now: number = Date.now()): string {
  const date = toDate(value);
  if (!date) return EMPTY;

  const diff = now - date.getTime();
  const abs = Math.abs(diff);
  if (abs < 10 * SECOND) return "just now";

  const amount = unitize(abs);
  return diff >= 0 ? `${amount} ago` : `in ${amount}`;
}

function unitize(abs: number): string {
  if (abs < MINUTE) return `${Math.floor(abs / SECOND)}s`;
  if (abs < HOUR) return `${Math.floor(abs / MINUTE)}m`;
  if (abs < DAY) return `${Math.floor(abs / HOUR)}h`;
  if (abs < WEEK) return `${Math.floor(abs / DAY)}d`;
  if (abs < MONTH) return `${Math.floor(abs / WEEK)}w`;
  if (abs < YEAR) return `${Math.floor(abs / MONTH)}mo`;
  return `${Math.floor(abs / YEAR)}y`;
}

/**
 * Uppercase calendar date, to sit next to the store's display type. The year is
 * printed only when it is not the current one.
 *
 *   shortDate("2026-09-03T12:00:00Z") -> "SEP 3"   (during 2026)
 *   shortDate("2024-11-20T12:00:00Z") -> "NOV 20, 2024"
 */
export function shortDate(value: DateInput, now: number = Date.now()): string {
  const date = toDate(value);
  if (!date) return EMPTY;

  const month = MONTHS[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const currentYear = new Date(now).getFullYear();

  return year === currentYear ? `${month} ${day}` : `${month} ${day}, ${year}`;
}

/**
 * The right form of a word for a count. Returns the word only, so the caller
 * decides how the number is rendered:
 *
 *   `${n} ${pluralize(n, "restock")}`      -> "3 restocks" / "1 restock"
 *   pluralize(n, "size", "sizes")
 */
export function pluralize(count: number, word: string, plural?: string): string {
  if (Math.abs(count) === 1) return word;
  if (plural !== undefined) return plural;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
}

/**
 * Shopify serves protocol-relative image URLs ("//cdn.shopify.com/…"), which do
 * not load from an https origin.
 *
 * A missing or blank source comes back undefined rather than an empty string,
 * so a caller can fall straight through to its own placeholder instead of
 * rendering an <img> that is guaranteed to break.
 */
export function resolveImage(src: string | null | undefined): string | undefined {
  if (typeof src !== "string") return undefined;
  const url = src.trim();
  if (url === "") return undefined;
  return url.startsWith("//") ? `https:${url}` : url;
}
